import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  TextField,
  TablePagination,
  Button,
  Box,
  InputAdornment,
  Modal,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import axios from "axios";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const VQTable = ({ vendors, isLoading, getVendorTenders }) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const [status, setStatus] = useState();
  const [reason, setReason] = useState();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [errors, setErrors] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    if (isEmpty(reason)) newErrors.reason = "Reason is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpen = (s) => {
    setOpen(true);
    setStatus(s);
  };
  const handleClose = () => {
    setOpen(false);
    setStatus("");
    setReason("");
  };

  const groupedData = useMemo(() => {
    const filtered =
      vendors &&
      vendors?.filter(
        (v) =>
          v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          v.mobile.includes(searchTerm) ||
          v.pan.toLowerCase().includes(searchTerm.toLowerCase()) ||
          v.gstin.toLowerCase().includes(searchTerm.toLowerCase())
      );

    return filtered?.reduce((acc, vendor) => {
      const tenderTitle = vendor.vendorTender.tender.title;
      if (!acc[tenderTitle]) acc[tenderTitle] = [];
      acc[tenderTitle].push(vendor);
      return acc;
    }, {});
  }, [vendors, searchTerm]);

  const handleSelectAllClick = (event, tenderVendors) => {
    const tenderVtIds = tenderVendors.map((v) => v.vendorTender.vtId);

    if (event.target.checked) {
      // Select all: merge existing selected with new ones and remove duplicates
      const newSelected = Array.from(new Set([...selected, ...tenderVtIds]));
      setSelected(newSelected);
    } else {
      // Deselect all: remove only the tender-specific ids
      const filtered = selected.filter((id) => !tenderVtIds.includes(id));
      setSelected(filtered);
    }
  };

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);
    }
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const paginatedData = useMemo(() => {
    const entries = Object.entries(groupedData || {});
    const flatData = entries.flatMap(([title, list]) => list);
    const paged = flatData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    const grouped = {};
    paged.forEach((vendor) => {
      const title = vendor.vendorTender.tender.title;
      if (!grouped[title]) grouped[title] = [];
      grouped[title].push(vendor);
    });
    return grouped;
  }, [groupedData, page, rowsPerPage]);

  const handleVendorTenderUpdate = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      const payload = selected.map((id) => ({
        vtId: id,
        status: status,
        username: storedUser.username,
        comment: reason,
      }));

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/vendor-tender`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setSelected([]);
      setOpen(false);
      getVendorTenders();
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Paper
      sx={{ width: "100%", overflow: "hidden", boxShadow: "none !important" }}
    >
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        {storedUser.userRole[0] === "BUYER" && (
          <Box>
            <Button
              className="accept-all"
              onClick={() => handleOpen("QSTAGE_1")}
            >
              Approve Selected
            </Button>
            <Button
              className="reject-all"
              onClick={() => handleOpen("DISQUALIFIED")}
            >
              Reject Selected
            </Button>
          </Box>
        )}
        {storedUser.userRole[0] === "TECHNICAL_HEAD" && (
          <Box>
            <Button
              className="accept-all"
              onClick={() => handleOpen("QSTAGE_2")}
            >
              Approve Selected
            </Button>
            <Button
              className="reject-all"
              onClick={() => handleOpen("DISQUALIFIED")}
            >
              Reject Selected
            </Button>
          </Box>
        )}
        {storedUser.userRole[0] === "PURCHASE_HEAD" && (
          <Box>
            <Button
             className="accept-all"
              onClick={() => handleOpen("QUALIFIED")}
            >
              Approve Selected
            </Button>
            <Button
              className="reject-all"
              onClick={() => handleOpen("DISQUALIFIED")}
            >
              Reject Selected
            </Button>
          </Box>
        )}
        <Box
          display="flex"
          justifyContent="end"
          alignItems="center"
          gap={1}
          p={2}
        >
          <TextField
            placeholder="Search"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
              "&:hover": {
                borderRadius: "12px",
                background: "#D8E9F0",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {/* <Button
            startIcon={<FilterListIcon />}
            sx={{
              height: 40,
              border: "1px solid #c7c7c7 !important",
              borderRadius: "12px",
              color: "#000",
              textTransform: "none",
              backgroundColor: "none",
              ":hover": {
                backgroundColor: "none",
              },
            }}
            onClick={() => {
              // Optional: Toggle a filter modal/panel if needed
              console.log("Filter button clicked");
            }}
          >
            Filter
          </Button> */}
        </Box>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selected.length > 0 &&
                    selected.length <
                      Object.values(paginatedData || {}).flat().length
                  }
                  checked={
                    selected.length > 0 &&
                    selected.length ===
                      Object.values(paginatedData || {}).flat().length
                  }
                  onChange={(event) =>
                    handleSelectAllClick(
                      event,
                      Object.values(paginatedData || {}).flat()
                    )
                  }
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>PAN</TableCell>
              <TableCell>GSTIN</TableCell>
              <TableCell>View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : Object.keys(paginatedData || {}).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No rows
                </TableCell>
              </TableRow>
            ) : (
              Object.entries(paginatedData).map(([title, vendors]) => (
                <React.Fragment key={title}>
                  <TableRow sx={{ background: "#EAF4FF" }}>
                    <TableCell>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                      ></Typography>
                    </TableCell>
                    <TableCell colSpan={6}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {title}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {vendors.map((row) => {
                    const isItemSelected = isSelected(row.vendorTender.vtId);
                    return (
                      <TableRow
                        key={row.vtqId}
                        hover
                        role="checkbox"
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleClick(row.vendorTender.vtId)}
                          />
                        </TableCell>
                        <TableCell>
                          <strong>{row.name}</strong>
                        </TableCell>
                        <TableCell>{row.mobile}</TableCell>
                        <TableCell>{row.pan}</TableCell>
                        <TableCell>{row.gstin}</TableCell>
                        <TableCell>
                          <Link to={`/dashboard/vendor/${row.vtqId}`} onClick={() => localStorage.setItem("action", "pending")}>
                            View
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={Object.values(groupedData || {}).flat().length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={10}
        rowsPerPageOptions={[]}
      />

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h5" fontWeight={"bold"}>
            {status === "DISQUALIFIED" ? "Reject the Vendor" : "Accept the Vendor"}
          </Typography>
          <label htmlFor="reason">
            Please give reason for {status === "DISQUALIFIED" ? "Reject" : "Accept"}ing the vendor*
          </label>
          <textarea
            className={`${errors.reason && "input-error"}`}
            name="reason"
            id="reason"
            onChange={(e) => {
              setReason(e.target.value)
              setErrors({reason: ""})
            }}
            rows={8}
          ></textarea>
          {errors.reason && <p className="error">{errors.reason}</p>}
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button className="btn-outlined" onClick={handleClose}>
              Close
            </Button>
            <Button
              className={
                status === "DISQUALIFIED"
                  ? "btn-outlined btn-reject"
                  : "btn-contained"
              }
              onClick={handleVendorTenderUpdate}
              disabled={btnLoading}
            >
              {btnLoading ? "Submitting..." : status === "DISQUALIFIED" ? "Reject" : "Accept"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Paper>
  );
};

export default VQTable;
